import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Card, CardContent, TextField, MenuItem, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import { reportingCapacityAPI } from '../services/reportingCapacityApi';

const num = (n) => Number(n || 0).toLocaleString();
const when = (d) => (d ? new Date(d).toLocaleString() : '-');
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const LIMITS = [50, 100, 250, 500];
const statusColor = (s) => (s === 'SUCCESS' ? 'success' : s === 'FAILED' ? 'error' : 'default');

function Stat({ label, value, color }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant={typeof value === 'string' ? 'subtitle1' : 'h5'} component="div" color={color} sx={{ fontWeight: typeof value === 'string' ? 500 : undefined }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function ReportingEtlRuns() {
  const [summary, setSummary] = useState(null);
  const [runs, setRuns] = useState([]);
  const [errors, setErrors] = useState([]);
  const [limit, setLimit] = useState(100);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    setLoading(true); setError('');
    return Promise.all([reportingCapacityAPI.etlSummary(), reportingCapacityAPI.etlRuns(limit), reportingCapacityAPI.etlErrors(limit)])
      .then(([s, r, e]) => { setSummary(s.data); setRuns(r.data); setErrors(e.data); })
      .catch((err) => setError(errText(err, 'Could not load ETL runs')))
      .finally(() => setLoading(false));
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  const shown = runs.filter((r) => !status || r.status === status);
  const statuses = [...new Set(runs.map((r) => r.status))].sort();

  return (
    <Box>
      <Typography variant="h5" gutterBottom>ETL runs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Health of the jobs that copy order and storage figures from each STARLIMS server into the reporting database.
      </Typography>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Rows to load" value={limit} onChange={(e) => setLimit(Number(e.target.value))} sx={{ minWidth: 130 }}>
          {LIMITS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !summary && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {summary && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2, mb: 2 }}>
            <Stat label="Last run" value={when(summary.last_run)} />
            <Stat label="Total runs" value={num(summary.total_runs)} />
            <Stat label="Successful" value={num(summary.successful)} color="success.main" />
            <Stat label="Failed" value={num(summary.failed)} color={summary.failed > 0 ? 'error.main' : undefined} />
            <Stat label="Rows inserted" value={num(summary.total_rows)} />
            <Stat label="Errors logged" value={num(summary.total_errors)} color={summary.total_errors > 0 ? 'error.main' : undefined} />
          </Box>
          <Paper sx={{ mb: 2, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Latest run per server</Typography>
            <TableContainer>
              <Table size="small" aria-label="Latest run per server">
                <TableHead><TableRow><TableCell>Server</TableCell><TableCell>Last run</TableCell><TableCell align="right">Rows inserted</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {summary.latest_per_server.map((r) => (
                    <TableRow key={r.run_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{r.source_server}</TableCell><TableCell>{when(r.start_time)}</TableCell>
                      <TableCell align="right">{num(r.rows_inserted)}</TableCell><TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                    </TableRow>
                  ))}
                  {summary.latest_per_server.length === 0 && <TableRow><TableCell colSpan={4}>No runs recorded.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <Paper sx={{ mb: 2, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Run log ({shown.length}{status ? ` ${status}` : ''} of the last {runs.length})</Typography>
            <TableContainer sx={{ maxHeight: 460 }}>
              <Table size="small" stickyHeader aria-label="Run log">
                <TableHead><TableRow><TableCell>Run</TableCell><TableCell>Job</TableCell><TableCell>Server</TableCell><TableCell>Started</TableCell><TableCell>Ended</TableCell><TableCell align="right">Rows</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                <TableBody>
                  {shown.map((r) => (
                    <TableRow key={r.run_id} hover>
                      <TableCell><Typography variant="caption" color="text.secondary">{r.run_id}</Typography></TableCell>
                      <TableCell>{r.job_name || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{r.source_server}</TableCell>
                      <TableCell>{when(r.start_time)}</TableCell><TableCell>{when(r.end_time)}</TableCell>
                      <TableCell align="right">{num(r.rows_inserted)}</TableCell>
                      <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} variant={r.status === 'SUCCESS' || r.status === 'FAILED' ? 'filled' : 'outlined'} /></TableCell>
                    </TableRow>
                  ))}
                  {shown.length === 0 && <TableRow><TableCell colSpan={7}>No runs to show.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {errors.length > 0 && (
            <Paper sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" color="error" sx={{ p: 2, pb: 0 }}>Error log ({errors.length})</Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader aria-label="Error log">
                  <TableHead><TableRow><TableCell>Time</TableCell><TableCell>Job</TableCell><TableCell>Server</TableCell><TableCell>Database</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
                  <TableBody>
                    {errors.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell>{when(e.error_time)}</TableCell><TableCell>{e.job_name || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{e.source_server}</TableCell><TableCell>{e.database_name}</TableCell>
                        <TableCell sx={{ color: 'error.main', wordBreak: 'break-word' }}>{e.error_message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
